/**
 * Which help article answers "what is this page?" for each route. The `?`
 * drawer opens the article for the deepest matched route.
 *
 * Keys are TanStack route ids (see routeTree.gen.ts). Add an entry when you
 * add a route; the help test fails for a route without one.
 */

export const DEFAULT_HELP_SLUG = 'welcome'

type Resolver = string | ((search: Record<string, unknown>) => string)

export const HELP_CONTEXT: Record<string, Resolver> = {
  '/': 'dashboard',
  '/review': 'review',
  '/kit': DEFAULT_HELP_SLUG,

  '/applications/': 'applications',
  '/applications/$appId/': 'applications',
  '/applications/$appId/event-types': 'event-types',
  '/applications/$appId/templates': 'templates-overview',
  '/applications/$appId/senders': 'senders',
  '/applications/$appId/keys': 'api-keys',
  '/applications/$appId/webhooks': 'webhooks',
  '/applications/$appId/service-bus/': 'service-bus-sources',
  '/applications/$appId/service-bus/$sourceId': (s) =>
    s.tab === 'dead-letters'
      ? 'service-bus-dead-letters'
      : s.tab === undefined || s.tab === 'settings'
        ? 'service-bus-mapping'
        : 'service-bus-sources',
  '/applications/$appId/variables': 'application-variables',
  '/applications/$appId/settings': 'application-settings',

  '/templates/': 'templates-overview',
  '/templates/$templateId/': 'template-studio',
  '/templates/$templateId/versions': 'template-versions',
  '/partials/': 'partials-and-layouts',
  '/partials/$partialId': 'partials-and-layouts',

  '/event-types/': 'event-types',
  // The Rules tab is the default; Schema, Samples, and Settings describe the event type itself.
  '/event-types/$eventTypeId': (s) =>
    s.tab === 'schema' || s.tab === 'samples' || s.tab === 'settings' ? 'event-types' : 'rules',

  '/messages/': 'message-log',
  '/messages/$messageId': (s) => (s.tab === 'timeline' ? 'message-statuses' : 'message-log'),
  '/requests/$requestId': 'message-log',
  '/timelines': 'timelines',
  '/suppressions': 'suppressions',
  '/audit': 'audit-log',
  '/system': 'system-status',

  '/help/': DEFAULT_HELP_SLUG,
  '/help/$slug': DEFAULT_HELP_SLUG,
  '/help/whats-new': DEFAULT_HELP_SLUG,
}

/** Layout-only routes that never end a match. They need no entry. */
export const LAYOUT_ROUTE_IDS = [
  '__root__',
  '/applications',
  '/applications/$appId',
  '/applications/$appId/service-bus',
  '/templates',
  '/templates/$templateId',
  '/partials',
  '/event-types',
  '/messages',
  '/help',
]

/** The help article for a route id (the deepest match) and its search params. */
export function helpSlugFor(
  routeId: string | undefined,
  search: Record<string, unknown> = {},
): string {
  const r = routeId ? HELP_CONTEXT[routeId] : undefined
  if (r === undefined) return DEFAULT_HELP_SLUG
  return typeof r === 'string' ? r : r(search)
}

/** Every slug the context map can return, for validation. */
export function contextSlugs(): string[] {
  const out = new Set<string>([DEFAULT_HELP_SLUG])
  const probes: Record<string, unknown>[] = [
    {},
    { tab: 'rules' },
    { tab: 'schema' },
    { tab: 'samples' },
    { tab: 'settings' },
    { tab: 'timeline' },
    { tab: 'dead-letters' },
  ]
  for (const r of Object.values(HELP_CONTEXT)) {
    if (typeof r === 'string') out.add(r)
    else for (const p of probes) out.add(r(p))
  }
  return [...out].sort()
}
